import { z } from 'zod';
import { TimezoneSchema } from './common';
import { ChannelSchema } from './messaging';
import { REVIEW_CHANNEL_PREFERENCES } from './reviews';

/**
 * Onboarding state (tenants.onboarding_state). Step keys are shared by the Basic guided wizard and
 * the (future) Pro conversational AI onboarding. Additive only: older rows (seed) keep parsing.
 *
 * Basic guided wizard (both plans until the AI onboarding exists):
 *   location        1) store details + Google review link
 *   review_settings 2) sender name & channel
 *   contacts        3) first contacts (quick add / import)
 *   test_request    4) a test review request to yourself
 *   done            5) finished → Home
 * `business_profile` and `plan` are completed during /setup (before payment).
 */
export const ONBOARDING_STEPS = [
  'business_profile',
  'location',
  'plan',
  'contacts',
  'review_settings',
  'ai_interview',
  'schema',
  'automations',
  'dashboard',
  'test_request',
  'done',
] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

/** Ordered steps of the Basic guided wizard (excluding the final `done`). */
export const BASIC_ONBOARDING_STEPS = [
  'location',
  'review_settings',
  'contacts',
  'test_request',
] as const satisfies readonly OnboardingStep[];
export type BasicOnboardingStep = (typeof BASIC_ONBOARDING_STEPS)[number];

export const ONBOARDING_PATHS = ['basic', 'ai'] as const;
export type OnboardingPath = (typeof ONBOARDING_PATHS)[number];

export const OnboardingStateSchema = z.object({
  currentStep: z.enum(ONBOARDING_STEPS).default('business_profile'),
  completedSteps: z.array(z.enum(ONBOARDING_STEPS)).default([]),
  skippedSteps: z.array(z.enum(ONBOARDING_STEPS)).default([]),
  conversationId: z.string().nullable().default(null),
  completedAt: z.string().nullable().default(null),
  /** Which onboarding experience the tenant is using (absent = basic). */
  path: z.enum(ONBOARDING_PATHS).optional(),
  startedAt: z.string().nullable().optional(),
  testRequestSentAt: z.string().nullable().optional(),
  /** Pro conversational onboarding state (AiOnboardingStateSchema below; parsed by its owner). */
  ai: z.unknown().optional(),
});
export type OnboardingState = z.infer<typeof OnboardingStateSchema>;

export function initialOnboardingState(): OnboardingState {
  return OnboardingStateSchema.parse({});
}

export const ONBOARDING_STEP_STATUSES = ['done', 'skipped', 'current', 'pending'] as const;
export type OnboardingStepStatus = (typeof ONBOARDING_STEP_STATUSES)[number];

/** Default store snapshot shown/edited by the wizard (subset of `locations`). */
export const OnboardingStoreSchema = z.object({
  locationId: z.uuid().nullable(),
  name: z.string().nullable(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
  googleReviewUrl: z.string().nullable(),
  senderName: z.string().nullable(),
  channel: z.enum(REVIEW_CHANNEL_PREFERENCES),
  smsTemplate: z.object({ el: z.string(), en: z.string() }),
});
export type OnboardingStore = z.infer<typeof OnboardingStoreSchema>;

export const OnboardingStatusSchema = z.object({
  path: z.enum(ONBOARDING_PATHS),
  /** True when the plan includes the conversational AI onboarding (slot in the UI). */
  aiAvailable: z.boolean(),
  currentStep: z.enum(ONBOARDING_STEPS),
  steps: z.array(
    z.object({ key: z.enum(BASIC_ONBOARDING_STEPS), status: z.enum(ONBOARDING_STEP_STATUSES) }),
  ),
  progress: z.object({ done: z.number().int(), total: z.number().int() }),
  /** All steps resolved (done or skipped). */
  allResolved: z.boolean(),
  /** Onboarding finished (the Home progress card disappears). */
  complete: z.boolean(),
  completedAt: z.string().nullable(),
  testRequestSentAt: z.string().nullable(),
  contactsCount: z.number().int(),
  store: OnboardingStoreSchema,
  businessName: z.string(),
});
export type OnboardingStatus = z.infer<typeof OnboardingStatusSchema>;

/** Accepts the review link formats Google hands out (g.page/r/…, search.google.com/local/writereview?placeid=…, maps.app.goo.gl/…). */
export const GOOGLE_REVIEW_URL_HOSTS = [
  'g.page',
  'search.google.com',
  'www.google.com',
  'google.com',
  'maps.google.com',
  'maps.app.goo.gl',
  'goo.gl',
  'g.co',
  'www.google.gr',
  'google.gr',
] as const;

export function isLikelyGoogleReviewUrl(value: string): boolean {
  try {
    const u = new URL(value);
    if (u.protocol !== 'https:') return false;
    return (GOOGLE_REVIEW_URL_HOSTS as readonly string[]).includes(u.hostname.toLowerCase());
  } catch {
    return false;
  }
}

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const OnboardingStoreRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  address: optionalText(300),
  phone: optionalText(32),
  googleReviewUrl: z
    .union([
      z.literal('').transform(() => null),
      z.url().refine(isLikelyGoogleReviewUrl, { message: 'not_a_google_review_link' }),
    ])
    .nullable()
    .optional(),
});
export type OnboardingStoreRequest = z.input<typeof OnboardingStoreRequestSchema>;

/** SMS alphanumeric sender id: 3–11 latin letters/digits (spaces allowed inside). */
export const SmsSenderNameSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9][A-Za-z0-9 ]{1,9}[A-Za-z0-9]$/, 'sender_name_format');

export const OnboardingSenderRequestSchema = z.object({
  senderName: SmsSenderNameSchema,
  channel: z.enum(REVIEW_CHANNEL_PREFERENCES),
});
export type OnboardingSenderRequest = z.input<typeof OnboardingSenderRequestSchema>;

export const OnboardingStepActionSchema = z.enum(['complete', 'skip']);

export const OnboardingTestRequestSchema = z.object({
  channel: ChannelSchema,
  /** Your own phone (SMS) or email address. */
  to: z.string().trim().min(3).max(254),
  locale: z.enum(['el', 'en']).optional(),
});
export type OnboardingTestRequest = z.input<typeof OnboardingTestRequestSchema>;

export const OnboardingTestResponseSchema = z.object({
  deliveryId: z.uuid(),
  channel: ChannelSchema,
  status: z.string(),
  creditsCharged: z.number().int(),
  /** Masked destination (never echo full PII back into logs). */
  to: z.string(),
  preview: z.object({ subject: z.string().nullable(), body: z.string() }),
  replayed: z.boolean(),
  onboarding: OnboardingStatusSchema,
});
export type OnboardingTestResponse = z.infer<typeof OnboardingTestResponseSchema>;

/** GET /onboarding/test-preview?locale= — exactly what the step-4 test message will contain. */
export const OnboardingTestPreviewQuerySchema = z.object({
  locale: z.enum(['el', 'en']).optional(),
});
export const OnboardingTestPreviewSchema = z.object({
  locale: z.enum(['el', 'en']),
  sms: z.string(),
  email: z.object({ subject: z.string(), body: z.string() }),
  /** Sender shown on the SMS / email "from". */
  senderName: z.string(),
  /**
   * The only mobiles this user may send the test SMS to (E.164): their verified sign-in phone and
   * the business's location phones. Test emails go only to the sign-in address.
   */
  testPhones: z.array(z.string()),
});
export type OnboardingTestPreview = z.infer<typeof OnboardingTestPreviewSchema>;

// ─── Pro conversational AI onboarding (spec §7; feature `ai.onboarding`) ─────────────────────
// State lives in tenants.onboarding_state.ai (+ an `onboarding` conversation for the transcript).

/** Spec §7.2 state machine (registered → … → ready). The first three happen in /setup. */
export const AI_ONBOARDING_PHASES = [
  'registered',
  'payment_pending',
  'subscription_active',
  'discovery',
  'source_selection',
  'import_mapping',
  'minimal_setup',
  'custom_schema_setup',
  'schema_preview',
  'schema_approved',
  'initial_dashboard_draft',
  'workflow_recommendations',
  'ready',
] as const;
export type AiOnboardingPhase = (typeof AI_ONBOARDING_PHASES)[number];

/** Orthogonal run status (spec: failed, paused, needs_user_input, resumable). */
export const AI_ONBOARDING_STATUSES = [
  'in_progress',
  'needs_user_input',
  'paused',
  'resumable',
  'failed',
  'completed',
] as const;
export type AiOnboardingStatus = (typeof AI_ONBOARDING_STATUSES)[number];

/** What the assistant is waiting for (the card shown with the last message). */
export const AI_ONBOARDING_QUESTIONS = [
  'profile',
  'objectives',
  'source',
  'import',
  'channels',
  'proposal',
  'dashboard',
  'activation',
] as const;
export type AiOnboardingQuestion = (typeof AI_ONBOARDING_QUESTIONS)[number];

export const ONBOARDING_OBJECTIVES = ['reviews', 'messaging', 'crm', 'visits', 'loyalty'] as const;
export type OnboardingObjective = (typeof ONBOARDING_OBJECTIVES)[number];

export const ONBOARDING_DATA_SOURCES = ['import', 'scratch', 'contacts_only'] as const;
export type OnboardingDataSource = (typeof ONBOARDING_DATA_SOURCES)[number];

/** Does the business have permission to send marketing to its existing customers? */
export const MARKETING_PERMISSIONS = ['all', 'some', 'none', 'unknown'] as const;
export type MarketingPermission = (typeof MARKETING_PERMISSIONS)[number];

/** Same list as tenants BUSINESS_CATEGORIES (kept local to avoid an import cycle). */
export const AI_ONBOARDING_CATEGORIES = [
  'barbershop',
  'hair_salon',
  'beauty_salon',
  'gym',
  'restaurant',
  'cafe',
  'retail',
  'auto_repair',
  'clinic',
  'other',
] as const;
export type AiOnboardingCategory = (typeof AI_ONBOARDING_CATEGORIES)[number];

export const AiOnboardingProfileSchema = z.object({
  businessName: z.string().max(120).nullable().default(null),
  category: z.enum(AI_ONBOARDING_CATEGORIES).nullable().default(null),
  locale: z.enum(['el', 'en']).nullable().default(null),
  timezone: z.string().max(64).nullable().default(null),
  country: z.string().max(2).nullable().default(null),
  objectives: z.array(z.enum(ONBOARDING_OBJECTIVES)).nullable().default(null),
  dataSource: z.enum(ONBOARDING_DATA_SOURCES).nullable().default(null),
  channels: z.object({ sms: z.boolean(), email: z.boolean() }).nullable().default(null),
  marketingPermission: z.enum(MARKETING_PERMISSIONS).nullable().default(null),
  /** The import step was finished or skipped (import path only). */
  importResolved: z.boolean().default(false),
});
export type AiOnboardingProfile = z.infer<typeof AiOnboardingProfileSchema>;

// ── SchemaProposal (spec §7.4) ──────────────────────────────────────────────

/** Field types onboarding may propose (no PII contact types; reference/object need the studio). */
export const PROPOSAL_FIELD_TYPES = [
  'text',
  'long_text',
  'integer',
  'decimal',
  'money',
  'boolean',
  'date',
  'enum',
  'multi_enum',
] as const;

const ProposalKey = z.string().regex(/^[a-z][a-z0-9_]{0,47}$/);

export const ProposalFieldSpecSchema = z.object({
  key: ProposalKey,
  label: z.string().min(1).max(120),
  labelEn: z.string().max(120).optional(),
  type: z.enum(PROPOSAL_FIELD_TYPES),
  allowedValues: z.array(z.string().min(1).max(80)).max(30).optional(),
  piiClassification: z.enum(['none', 'personal']).default('none'),
  /** Sample value for the preview panel (never real customer data). */
  example: z.string().max(80).optional(),
});
export type ProposalFieldSpec = z.infer<typeof ProposalFieldSpecSchema>;

export const ProposalEventSpecSchema = z.object({
  key: ProposalKey,
  label: z.string().min(1).max(120),
  labelEn: z.string().max(120).optional(),
  hasValue: z.boolean().default(false),
  countsAsVisit: z.boolean().default(false),
  /** A sale / purchase record (revenue, purchase count, last purchase). Unset = server rule. */
  countsAsPurchase: z.boolean().optional(),
});
export type ProposalEventSpec = z.infer<typeof ProposalEventSpecSchema>;

export const ProposalObjectSpecSchema = z.object({
  key: ProposalKey,
  label: z.string().min(1).max(80),
  labelPlural: z.string().min(1).max(80),
  labelEn: z.string().max(80).optional(),
  displayFieldLabel: z.string().min(1).max(120),
});
export type ProposalObjectSpec = z.infer<typeof ProposalObjectSpecSchema>;

/** Metrics the platform computes from events (informational; nothing to apply). */
export const ProposalMetricSpecSchema = z.object({
  key: ProposalKey,
  label: z.string().min(1).max(120),
  basedOn: z.array(z.string().max(64)).max(5).default([]),
});
export type ProposalMetricSpec = z.infer<typeof ProposalMetricSpecSchema>;

/**
 * Home-page ideas picked during AI onboarding. Stored on the onboarding draft only: nothing builds
 * a dashboard from them (the AI dashboard is designed later in its own studio), so the copy
 * presents them as ideas, never as a promise that the home page will change.
 */
export const ONBOARDING_WIDGET_KEYS = [
  'visits_trend',
  'inactive_customers',
  'upcoming_birthdays',
  'review_funnel',
  'revenue_by_month',
  'top_customers',
  'expiring_memberships',
  'new_customers',
  'service_mix',
] as const;
export type OnboardingWidgetKey = (typeof ONBOARDING_WIDGET_KEYS)[number];

export const ProposalWidgetSpecSchema = z.object({
  key: z.enum(ONBOARDING_WIDGET_KEYS),
  title: z.string().min(1).max(120),
  kind: z.enum(['kpi', 'trend', 'list', 'funnel']),
});
export type ProposalWidgetSpec = z.infer<typeof ProposalWidgetSpecSchema>;

/** Automation templates worth enabling (automations module activates them with its wizard). */
export const ProposalWorkflowSpecSchema = z.object({
  templateKey: z.enum([
    'birthday',
    'new_year',
    'easter',
    'name_day',
    'win_back',
    'thank_you_purchase',
    'appointment_follow_up',
    'membership_expiry',
    'review_request_after_event',
  ]),
  title: z.string().min(1).max(120),
  /** Data the automation needs (shown as "needs: birthday, visits"). */
  requires: z.array(z.string().max(40)).max(5).default([]),
});
export type ProposalWorkflowSpec = z.infer<typeof ProposalWorkflowSpecSchema>;

export const PROPOSAL_ITEM_DECISIONS = ['pending', 'accepted', 'skipped'] as const;
export type ProposalItemDecision = (typeof PROPOSAL_ITEM_DECISIONS)[number];

const item = <S extends z.ZodType>(spec: S) =>
  z.object({
    /** Stable id inside the proposal, e.g. `field:preferred_barber`. */
    id: z.string().max(80),
    decision: z.enum(PROPOSAL_ITEM_DECISIONS),
    /** Already present in the tenant schema: shown, never applied twice. */
    exists: z.boolean().default(false),
    /** Why it is useful for this business (one sentence). */
    reason: z.string().max(300),
    spec,
  });

export const ProposalFieldItemSchema = item(ProposalFieldSpecSchema);
export const ProposalEventItemSchema = item(ProposalEventSpecSchema);
export const ProposalObjectItemSchema = item(ProposalObjectSpecSchema);
export const ProposalMetricItemSchema = item(ProposalMetricSpecSchema);
export const ProposalWidgetItemSchema = item(ProposalWidgetSpecSchema);
export const ProposalWorkflowItemSchema = item(ProposalWorkflowSpecSchema);
export type ProposalFieldItem = z.infer<typeof ProposalFieldItemSchema>;
export type ProposalEventItem = z.infer<typeof ProposalEventItemSchema>;
export type ProposalObjectItem = z.infer<typeof ProposalObjectItemSchema>;
export type ProposalWidgetItem = z.infer<typeof ProposalWidgetItemSchema>;
export type ProposalWorkflowItem = z.infer<typeof ProposalWorkflowItemSchema>;

export const SCHEMA_PROPOSAL_REVIEW_STATUSES = ['pending', 'applied', 'rejected'] as const;

export const AppliedProposalSchema = z.object({
  schemaVersion: z.number().int(),
  schemaProposalId: z.string().nullable(),
  fieldKeys: z.array(z.string()),
  eventKeys: z.array(z.string()),
  objectKeys: z.array(z.string()),
  at: z.string(),
});
export type AppliedProposal = z.infer<typeof AppliedProposalSchema>;

export const OnboardingSchemaProposalSchema = z.object({
  proposalId: z.uuid(),
  onboardingRunId: z.uuid(),
  businessType: z.enum(AI_ONBOARDING_CATEGORIES),
  source: z.enum(['ai', 'template']),
  createdAt: z.string(),
  status: z.enum(SCHEMA_PROPOSAL_REVIEW_STATUSES),
  fieldDefinitions: z.array(ProposalFieldItemSchema),
  eventDefinitions: z.array(ProposalEventItemSchema),
  objectDefinitions: z.array(ProposalObjectItemSchema),
  derivedMetrics: z.array(ProposalMetricItemSchema),
  recommendedWidgets: z.array(ProposalWidgetItemSchema),
  recommendedWorkflows: z.array(ProposalWorkflowItemSchema),
  assumptions: z.array(z.string().max(300)),
  warnings: z.array(z.string().max(300)),
  requiresApproval: z.literal(true),
  applied: AppliedProposalSchema.nullable().default(null),
});
export type OnboardingSchemaProposal = z.infer<typeof OnboardingSchemaProposalSchema>;

// ── transcript ───────────────────────────────────────────────────────────────

export const AiOnboardingCardSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('profile'), missing: z.array(z.string()) }),
  z.object({ type: z.literal('objectives') }),
  z.object({ type: z.literal('source') }),
  z.object({ type: z.literal('import') }),
  z.object({ type: z.literal('channels') }),
  z.object({ type: z.literal('proposal'), proposalId: z.uuid() }),
  z.object({ type: z.literal('dashboard'), proposalId: z.uuid().nullable() }),
  z.object({ type: z.literal('activation'), proposalId: z.uuid().nullable() }),
  z.object({ type: z.literal('summary') }),
]);
export type AiOnboardingCard = z.infer<typeof AiOnboardingCardSchema>;

export const AiOnboardingMessageSchema = z.object({
  id: z.uuid(),
  role: z.enum(['assistant', 'user']),
  text: z.string(),
  card: AiOnboardingCardSchema.nullable(),
  /** The answer a user message carried (chips/cards), for display. */
  answer: z.record(z.string(), z.unknown()).nullable(),
  /** Where the assistant text came from (model reply or built-in copy). */
  source: z.enum(['model', 'template', 'user']),
  createdAt: z.string(),
});
export type AiOnboardingMessage = z.infer<typeof AiOnboardingMessageSchema>;

export const AI_ONBOARDING_RAIL = [
  'profile',
  'objectives',
  'data',
  'schema',
  'dashboard',
  'activation',
] as const;
export type AiOnboardingRailStep = (typeof AI_ONBOARDING_RAIL)[number];

export const AiOnboardingStateSchema = z.object({
  /** onboardingRunId: idempotency scope of this onboarding (spec §7.2). */
  runId: z.uuid(),
  phase: z.enum(AI_ONBOARDING_PHASES),
  status: z.enum(AI_ONBOARDING_STATUSES),
  question: z.enum(AI_ONBOARDING_QUESTIONS).nullable(),
  profile: AiOnboardingProfileSchema,
  proposal: OnboardingSchemaProposalSchema.nullable().default(null),
  /** proposalId → result (idempotent apply). */
  applied: z.record(z.string(), AppliedProposalSchema).default({}),
  dashboard: z
    .object({ widgets: z.array(z.enum(ONBOARDING_WIDGET_KEYS)), skipped: z.boolean() })
    .nullable()
    .default(null),
  activation: z
    .object({ automations: z.array(z.string()), autoSendReviews: z.boolean().nullable() })
    .nullable()
    .default(null),
  creditsUsed: z.number().int().default(0),
  /** Client message ids already processed (double-click / retry dedupe). */
  handled: z.array(z.string()).default([]),
  importStartedAt: z.string().nullable().default(null),
  startedAt: z.string(),
  updatedAt: z.string(),
  pausedAt: z.string().nullable().default(null),
  completedAt: z.string().nullable().default(null),
  failure: z.object({ code: z.string(), at: z.string() }).nullable().default(null),
  /** Milestones reached, oldest first (spec §7.2 transitions, for resume/audit). */
  phaseLog: z.array(z.object({ phase: z.enum(AI_ONBOARDING_PHASES), at: z.string() })).default([]),
  /** The user chose the classic guided wizard instead (the chat stays resumable). */
  preferBasic: z.boolean().default(false),
});
export type AiOnboardingState = z.infer<typeof AiOnboardingStateSchema>;

export const AiOnboardingViewSchema = z.object({
  available: z.boolean(),
  started: z.boolean(),
  canEdit: z.boolean(),
  state: AiOnboardingStateSchema.nullable(),
  messages: z.array(AiOnboardingMessageSchema),
  rail: z.array(
    z.object({
      key: z.enum(AI_ONBOARDING_RAIL),
      status: z.enum(['done', 'current', 'pending', 'skipped']),
    }),
  ),
  contactsCount: z.number().int(),
  lastImport: z
    .object({
      id: z.uuid(),
      filename: z.string(),
      status: z.string(),
      created: z.number().int(),
      completedAt: z.string().nullable(),
    })
    .nullable(),
  reviews: z.object({
    googleReviewUrl: z.string().nullable(),
    senderName: z.string().nullable(),
    autoSend: z.boolean(),
    channel: z.string(),
  }),
  aiCredits: z.number().int(),
});
export type AiOnboardingView = z.infer<typeof AiOnboardingViewSchema>;

/** POST /onboarding/ai/answer — one user turn. `clientMessageId` makes retries idempotent. */
export const AiOnboardingAnswerSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('profile'),
    businessName: z.string().trim().min(2).max(120).optional(),
    category: z.enum(AI_ONBOARDING_CATEGORIES),
    locale: z.enum(['el', 'en']).optional(),
    timezone: TimezoneSchema.optional(),
  }),
  z.object({
    kind: z.literal('objectives'),
    objectives: z.array(z.enum(ONBOARDING_OBJECTIVES)).min(1).max(5),
  }),
  z.object({ kind: z.literal('source'), source: z.enum(ONBOARDING_DATA_SOURCES) }),
  z.object({ kind: z.literal('import'), action: z.enum(['done', 'skip']) }),
  z.object({
    kind: z.literal('channels'),
    sms: z.boolean(),
    email: z.boolean(),
    marketingPermission: z.enum(MARKETING_PERMISSIONS),
  }),
  z.object({ kind: z.literal('message'), text: z.string().trim().min(1).max(1000) }),
  z.object({
    kind: z.literal('dashboard'),
    widgets: z.array(z.enum(ONBOARDING_WIDGET_KEYS)).max(9),
    skip: z.boolean().default(false),
  }),
]);
export type AiOnboardingAnswer = z.input<typeof AiOnboardingAnswerSchema>;
export const AiOnboardingAnswerRequestSchema = z.object({
  clientMessageId: z.string().min(8).max(64),
  answer: AiOnboardingAnswerSchema,
});
export type AiOnboardingAnswerRequest = z.input<typeof AiOnboardingAnswerRequestSchema>;

/** Per-item decisions on a proposal; edits may rename labels / adjust enum values only. */
export const ApplyProposalRequestSchema = z.object({
  decisions: z
    .array(
      z.object({
        itemId: z.string().max(80),
        decision: z.enum(['accepted', 'skipped']),
        edits: z
          .object({
            label: z.string().trim().min(1).max(120).optional(),
            allowedValues: z.array(z.string().trim().min(1).max(80)).min(1).max(30).optional(),
          })
          .optional(),
      }),
    )
    .max(60),
});
export type ApplyProposalRequest = z.input<typeof ApplyProposalRequestSchema>;

export const ApplyProposalResponseSchema = z.object({
  view: AiOnboardingViewSchema,
  applied: AppliedProposalSchema,
  replayed: z.boolean(),
});

export const AiOnboardingFinishRequestSchema = z.object({
  /** Automation templates the user wants to set up next (activated in the automations wizard). */
  automations: z.array(z.string().max(40)).max(10).default([]),
  /** Turn on "auto-send a review request when a customer is added" (reviews settings). */
  autoSendReviews: z.boolean().optional(),
});
export type AiOnboardingFinishRequest = z.input<typeof AiOnboardingFinishRequestSchema>;
